"use client";

import React from "react";
import type { UseFormReturn } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { GrantAccessClientSelect } from "../../components/GrantAccessClientSelect";
import type { GrantEmployeeAccessFormData } from "@/lib/types/auth/grant-access";
import type { ClientRecord } from "@/lib/types/auth/grant-access";

interface ClientsFieldSectionProps {
  form: UseFormReturn<GrantEmployeeAccessFormData>;
  displayedClients: ClientRecord[];
  clientSearchTerm: string;
  setClientSearchTerm: (value: string) => void;
  isClientSearching: boolean;
  clientsLoading: boolean;
  setContactIds: (contactIds: string[]) => void;
  setGrantToAllClients: (value: boolean) => void;
  isBulk?: boolean;
}

/**
 * Shared right-side "Clients" form section used by both the standalone
 * GrantEmployeeAccessModal and the in-stack GrantEmployeeAccessForm.
 *
 * Render output preserved exactly from the original inline JSX.
 */
export const ClientsFieldSection: React.FC<ClientsFieldSectionProps> = ({
  form,
  displayedClients,
  clientSearchTerm,
  setClientSearchTerm,
  isClientSearching,
  clientsLoading,
  setContactIds,
  setGrantToAllClients,
  isBulk,
}) => {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium">
          Clients {!form.watch("grantToAllClients") && <span className="text-red-500">*</span>}
        </label>
        <div className="flex items-center gap-2">
          <Checkbox
            id="grantToAllClients"
            checked={form.watch("grantToAllClients")}
            onCheckedChange={(checked) => setGrantToAllClients(!!checked)}
          />
          <label
            htmlFor="grantToAllClients"
            className="text-xs text-muted-foreground cursor-pointer"
          >
            Grant access to all
          </label>
        </div>
      </div>
      <GrantAccessClientSelect
        value={form.watch("contactIds") ?? []}
        onValueChange={setContactIds}
        options={displayedClients}
        searchValue={clientSearchTerm}
        onSearchChange={setClientSearchTerm}
        isSearching={isClientSearching}
        isLoading={clientsLoading}
        disabled={clientsLoading || form.watch("grantToAllClients")}
        grantToAllClients={form.watch("grantToAllClients")}
        isBulk={isBulk ?? false}
      />
      {form.formState.errors.contactIds && (
        <div className="text-xs text-red-600 mt-1 dark:text-red-400">
          {form.formState.errors.contactIds.message}
        </div>
      )}
    </div>
  );
};
